import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, EyeOff, Eye, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import SchemaEditor from "@/components/SchemaEditor";

/**
 * CategoryManagerDialog — modal that lets admins add / rename / activate / deactivate
 * categories AND edit each category's measurement_schema via the nested SchemaEditor.
 */
export default function CategoryManagerDialog({ onClose }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [expandedSlug, setExpandedSlug] = useState(null);
  const editInputRef = useCallback((node) => {
    if (node) { node.focus(); node.select(); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories", { params: { include_inactive: true } });
      setCats(data || []);
    } catch (e) {
      toast.error("could not load categories.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/categories", { name });
      setNewName("");
      await load();
      toast.success(`"${name}" added.`);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "could not add category.");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (c) => { setEditingSlug(c.slug); setEditingName(c.name); };
  const cancelEdit = () => { setEditingSlug(null); setEditingName(""); };

  const saveEdit = async (slug) => {
    const name = editingName.trim();
    if (!name) { cancelEdit(); return; }
    try {
      await api.patch(`/categories/${slug}`, { name });
      cancelEdit();
      await load();
      toast.success("category renamed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not rename.");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/categories/${c.slug}`, { is_active: !c.is_active });
      await load();
      toast.success(c.is_active ? `"${c.name}" made inactive.` : `"${c.name}" reactivated.`);
    } catch (e) {
      toast.error("could not update status.");
    }
  };

  const saveSchema = async (slug, schema) => {
    try {
      await api.patch(`/categories/${slug}`, { measurement_schema: schema });
      await load();
      toast.success("measurement fields saved.");
    } catch (e) {
      toast.error("could not save fields.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-auto"
      onClick={onClose}
      data-testid="category-manager-dialog"
    >
      <div className="bg-ivory border border-taupe max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <span className="editorial-label">taxonomy · categories</span>
            <h2 className="font-serif text-3xl lowercase text-charcoal mt-2">manage categories</h2>
            <p className="text-xs text-espresso mt-1">inactive categories disappear from the picker but stay attached to historic pieces. expand a category to edit its measurement fields.</p>
          </div>
          <button onClick={onClose} aria-label="close" className="text-taupe hover:text-charcoal p-1" data-testid="cat-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={create} className="flex items-end gap-3 mt-6">
          <div className="flex-1">
            <label className="editorial-label">new category name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. blazers"
              data-testid="cat-new-name"
              className="w-full bg-transparent border-b border-taupe py-2 mt-1 text-charcoal focus:outline-none focus:border-brass"
            />
          </div>
          <button
            type="submit"
            disabled={creating || !newName.trim()}
            data-testid="cat-add-btn"
            className="bg-brass hover:bg-espresso text-ivory px-4 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> add
          </button>
        </form>

        <div className="mt-8 border-t border-taupe/40 pt-4 max-h-[60vh] overflow-auto" data-testid="cat-list">
          {loading && <p className="text-xs text-taupe italic">loading…</p>}
          {!loading && cats.length === 0 && <p className="text-xs text-taupe italic">no categories yet.</p>}
          <ul className="divide-y divide-taupe/30">
            {cats.map((c) => (
              <CategoryRow
                key={c.slug}
                cat={c}
                isExpanded={expandedSlug === c.slug}
                editingSlug={editingSlug}
                editingName={editingName}
                editInputRef={editInputRef}
                onToggleExpand={() => setExpandedSlug(expandedSlug === c.slug ? null : c.slug)}
                onStartEdit={() => startEdit(c)}
                onCancelEdit={cancelEdit}
                onSaveEdit={() => saveEdit(c.slug)}
                onEditingNameChange={setEditingName}
                onToggleActive={() => toggleActive(c)}
                onSaveSchema={(schema) => saveSchema(c.slug, schema)}
              />
            ))}
          </ul>
        </div>

        <div className="border-t border-taupe/40 mt-6 pt-4 flex justify-end">
          <button onClick={onClose} data-testid="cat-done" className="border border-taupe text-charcoal hover:border-brass hover:text-brass px-5 py-2 text-xs tracking-[0.25em] uppercase">
            done
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ cat, isExpanded, editingSlug, editingName, editInputRef, onToggleExpand, onStartEdit, onCancelEdit, onSaveEdit, onEditingNameChange, onToggleActive, onSaveSchema }) {
  const isEditing = editingSlug === cat.slug;
  const fieldCount = cat.measurement_schema?.length || 0;
  return (
    <li className="py-3" data-testid={`cat-row-${cat.slug}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button onClick={onToggleExpand} className="text-taupe hover:text-brass p-0.5" aria-label={isExpanded ? "collapse" : "expand"} data-testid={`cat-expand-${cat.slug}`}>
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {isEditing ? (
            <input
              ref={editInputRef}
              type="text"
              value={editingName}
              onChange={(e) => onEditingNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSaveEdit();
                if (e.key === "Escape") onCancelEdit();
              }}
              data-testid={`cat-edit-input-${cat.slug}`}
              className="flex-1 bg-transparent border-b border-brass py-1 text-charcoal focus:outline-none"
            />
          ) : (
            <span className={`text-sm lowercase ${cat.is_active ? "text-charcoal" : "text-taupe line-through"}`}>{cat.name}</span>
          )}
          <span className="text-[0.55rem] tracking-widest uppercase text-taupe">
            · {fieldCount} field{fieldCount === 1 ? "" : "s"}
          </span>
          {!cat.is_active && (
            <span className="text-[0.55rem] tracking-widest uppercase border border-taupe px-1.5 py-0.5 text-taupe">inactive</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button onClick={onSaveEdit} className="p-1.5 border border-brass text-brass hover:bg-brass hover:text-ivory" aria-label="save" data-testid={`cat-save-${cat.slug}`}>
                <Check className="w-3 h-3" />
              </button>
              <button onClick={onCancelEdit} className="p-1.5 border border-taupe text-charcoal hover:border-red-700 hover:text-red-700" aria-label="cancel" data-testid={`cat-cancel-${cat.slug}`}>
                <X className="w-3 h-3" />
              </button>
            </>
          ) : (
            <>
              <button onClick={onStartEdit} className="p-1.5 border border-taupe text-charcoal hover:border-brass hover:text-brass" aria-label="rename" data-testid={`cat-rename-${cat.slug}`}>
                <Pencil className="w-3 h-3" />
              </button>
              <button
                onClick={onToggleActive}
                className={`p-1.5 border ${cat.is_active ? "border-taupe text-charcoal hover:border-red-700 hover:text-red-700" : "border-brass text-brass hover:bg-brass hover:text-ivory"}`}
                aria-label={cat.is_active ? "make inactive" : "reactivate"}
                data-testid={`cat-toggle-${cat.slug}`}
              >
                {cat.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
            </>
          )}
        </div>
      </div>
      {isExpanded && <SchemaEditor cat={cat} onSave={onSaveSchema} />}
    </li>
  );
}
